import {ChangeDetectionStrategy, Component, ElementRef, inject, OnInit} from '@angular/core';
import {
  CancelCommercialRelease,
  CommercialReleaseSummary,
  DeclareCommercialRelease,
  IE3ConsignmentProcessSummary
} from "@portbase/bezoekschip-service-typescriptmodels";
import {initializeCommercialRelease} from "../../../commercial-release/commercial-release.utils";
import {AppContext} from "../../../app-context";
import {checkValidity, closeEditModal, formDataSaved, sendCommand} from '../../../common/utils';

/**
 * Right-side sliding modal shell (`edit-modal.component.scss`'s default, non-`leftToRight` chrome)
 * that renders the new Details/History tabs for the NEW commercial-release overview, and owns the
 * send/cancel command logic itself (ported verbatim from the reverted `CommercialReleaseDeclarationComponent`).
 * Declared in `AppModule` - NOT `PortVisitModule` - because it renders `<app-commercial-release-modal-details>`
 * etc., which are only known to `AppModule`'s declarations. The trigger call sites
 * (`CommercialReleaseOverviewItemComponent`, `CommercialReleaseOverviewNewComponent`) stay in
 * `PortVisitModule` and reference this class only via a plain TypeScript import passed to
 * `openEditModal()`, not via a template selector - so the cross-module direction is safe.
 */
@Component({
  selector: 'app-commercial-release-modal',
  templateUrl: './commercial-release-modal.component.html',
  styleUrls: ['./commercial-release-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CommercialReleaseModalComponent implements OnInit {
  private element: ElementRef = inject(ElementRef);

  data: CommercialReleaseModalComponentData;

  release: CommercialReleaseSummary;
  isNewRelease: boolean;

  // Founder ask: an EXISTING release must always open read-only first, editable only via the
  // explicit Edit button below (`startEditing()`) - mirrors `MasterConsignmentDetailsComponent`'s
  // own `editMode` field, which likewise starts `true` only for a brand-new record
  // (`isNewConsignment`) and stays `false` for an existing one until `startEditing()` is called.
  // This is now a plain mutable field (was a role/status-only computed getter before), so a role-
  // eligible user CAN still view an existing release without immediately being dropped into edit mode.
  editMode: boolean;

  // Set by the Details tab's `selectedConsignmentChange` output and read by the Parties tab, so
  // Container operator's CRN-linked lookup there can scope to the same consignment the Details tab
  // just resolved (mirrors the old `app-select`'s `[refreshWatch]="selectedConsignment"`).
  selectedConsignment: IE3ConsignmentProcessSummary;

  appContext = AppContext;

  ngOnInit(): void {
    this.isNewRelease = !this.data.release?.id;
    this.release = this.isNewRelease ? initializeCommercialRelease() : this.data.release;
    this.editMode = this.isNewRelease;
  }

  // The former `editMode` getter's role/status checks, kept as-is and now used to gate the Edit
  // button (and reused by the "Save and send" visibility below) rather than driving `editMode`
  // directly.
  get canEdit(): boolean {
    return (AppContext.isCommercialReleaseDeclarant() || AppContext.isCommercialReleaseEditor())
      && this.release?.releaseStatus?.status !== 'DECLARED'
      && !AppContext.isHinterlandTerminal();
  }

  startEditing(): void {
    this.editMode = true;
  }

  sendDeclaration(): void {
    if (checkValidity(this.element)) {
      sendCommand('com.portbase.bezoekschip.common.api.commercialrelease.DeclareCommercialRelease', <DeclareCommercialRelease>{
        releaseData: this.release.releaseData,
        id: this.release.id,
        crn: this.release.crn
      }, () => {
        AppContext.registerSuccess('The declaration was sent successfully.');
        formDataSaved();
        closeEditModal();
      });
    }
  }

  cancelRelease(): void {
    sendCommand('com.portbase.bezoekschip.common.api.commercialrelease.CancelCommercialRelease',
      <CancelCommercialRelease>{id: this.release.id},
      () => AppContext.registerSuccess('The declaration was cancelled successfully.'));
  }
}

export interface CommercialReleaseModalComponentData {
  release?: CommercialReleaseSummary;
}
